"use client";

import { ChartOptions } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { ISavingPropositionFund } from "@portal/types/robot/ISavingPropositionFund";
import { chartJsTextColor } from "@portal/helpers/chartJs";

interface Props {
  propositionFunds: ISavingPropositionFund[] | undefined;
}

interface Slice {
  name: string;
  y: number;
  color: string;
}

const getColor = (index: number): string => {
  if (index === 0) return "#50c878";
  if (index === 1) return "#EF9224";
  if (index === 2) return "#7CB5EC";
  if (index === 3) return "#e0a507";
  if (index === 4) return "#f2ede9";
  return "#50c878";
};

/**
 * Chart.js version of the saving suggestion nested donut chart.
 * @param interface
 * @returns a chart.
 */
export const SavingSuggestionChart = ({ propositionFunds }: Props) => {
  const outer: Slice[] = [];
  const inner: Slice[] = [];

  propositionFunds?.forEach((fund, i) => {
    outer.push({ name: fund.name, y: fund.percentageOfTotal, color: getColor(i) });
  });

  propositionFunds?.forEach((fund, i) => {
    if (!fund.combinationChildFunds.length) {
      inner.push({ name: fund.name, y: fund.percentageOfTotal, color: getColor(i) });
    } else {
      fund.combinationChildFunds.forEach(cf => {
        inner.push({ name: cf.name, y: cf.percentageOfTotal, color: getColor(i) });
      });
    }
  });

  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "40%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: chartJsTextColor(),
          usePointStyle: true,
          pointStyle: "circle",
          generateLabels: () =>
            outer.map((slice, i) => ({
              text: slice.name,
              fillStyle: slice.color,
              strokeStyle: slice.color,
              fontColor: chartJsTextColor(),
              pointStyle: "circle",
              index: i,
            })),
        },
      },
      tooltip: {
        callbacks: {
          label: item => {
            const slice = (item.dataset.data as unknown as number[])[item.dataIndex];
            const names = item.datasetIndex === 0 ? outer : inner;
            return `${names[item.dataIndex]?.name}: ${slice.toFixed(1)}%`;
          },
        },
      },
    },
  };

  return (
    <div style={{ height: 380 }}>
      <Doughnut
        data={{
          datasets: [
            { data: outer.map(s => s.y), backgroundColor: outer.map(s => s.color), borderWidth: 2, weight: 0.4 },
            { data: inner.map(s => s.y), backgroundColor: inner.map(s => s.color), borderWidth: 2, weight: 0.6 },
          ],
        }}
        options={options}
      />
    </div>
  );
};
